/**
 * 고객 이용문의 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Form, Alert } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { inquiryService } from '../../services/inquiryService';
import { Inquiry, InquiryResponse } from '../../types/inquiry';

const InquiryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inquiryId = id ? parseInt(id, 10) : 0;

  const [responseText, setResponseText] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showResponseForm, setShowResponseForm] = useState(false);

  const { data: inquiry, isLoading } = useQuery({
    queryKey: ['inquiry', inquiryId],
    queryFn: () => inquiryService.getInquiry(inquiryId),
    enabled: !!inquiryId && inquiryId > 0,
  });

  const respondMutation = useMutation({
    mutationFn: (data: InquiryResponse) => inquiryService.respondToInquiry(inquiryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      setShowResponseForm(false);
      setResponseText('');
      setAttachments([]);
      alert('답변이 전송되었습니다.');
    },
    onError: (error: any) => {
      alert(`답변 전송 실패: ${error.message}`);
    },
  });

  const statusMutation = useMutation({
    mutationFn: (status: Inquiry['status']) => inquiryService.updateInquiryStatus(inquiryId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inquiry', inquiryId] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">대기중</Badge>;
      case 'in_progress':
        return <Badge bg="info">처리중</Badge>;
      case 'completed':
        return <Badge bg="success">완료</Badge>;
      case 'cancelled':
        return <Badge bg="secondary">취소</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleSubmitResponse = () => {
    if (!responseText.trim()) {
      alert('답변 내용을 입력해주세요.');
      return;
    }

    respondMutation.mutate({
      response: responseText,
      attachments: attachments.length > 0 ? attachments : undefined,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!inquiry) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>문의를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/inquiries')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>고객 이용문의 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/inquiries')} className="me-2">
              목록으로
            </Button>
            {inquiry.status === 'pending' && (
              <Button
                variant="info"
                onClick={() => statusMutation.mutate('in_progress')}
                className="me-2"
              >
                처리중으로 변경
              </Button>
            )}
            {inquiry.status !== 'completed' && inquiry.status !== 'cancelled' && (
              <Button variant="primary" onClick={() => setShowResponseForm(!showResponseForm)}>
                {showResponseForm ? '답변 작성 취소' : '답변 작성'}
              </Button>
            )}
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <div className="mb-3">
              <h3>{inquiry.subject}</h3>
              <div className="mt-2">
                {getStatusBadge(inquiry.status)}
                <Badge bg="secondary" className="ms-2">{inquiry.inquiry_type}</Badge>
              </div>
            </div>

            <div className="mb-3">
              <strong>고객 정보:</strong>
              {inquiry.member_name ? (
                <div className="mt-1">
                  <div>이름: {inquiry.member_name}</div>
                  {inquiry.member_email && <div>이메일: {inquiry.member_email}</div>}
                  <Badge bg="info" className="ms-2">회원</Badge>
                </div>
              ) : inquiry.guest_name ? (
                <div className="mt-1">
                  <div>이름: {inquiry.guest_name}</div>
                  {inquiry.guest_email && <div>이메일: {inquiry.guest_email}</div>}
                  <Badge bg="warning" className="ms-2">게스트</Badge>
                </div>
              ) : (
                <div className="mt-1">
                  <span>비회원</span>
                </div>
              )}
            </div>

            <div className="mb-3">
              <strong>등록일:</strong> {new Date(inquiry.created_at).toLocaleString('ko-KR')}
            </div>

            {inquiry.responded_at && (
              <div className="mb-3">
                <strong>답변일:</strong> {new Date(inquiry.responded_at).toLocaleString('ko-KR')}
                {inquiry.responder_name && (
                  <span className="ms-2">(답변자: {inquiry.responder_name})</span>
                )}
              </div>
            )}

            {inquiry.attachments && inquiry.attachments.length > 0 && (
              <div className="mb-3">
                <strong>첨부 파일:</strong>
                <div className="mt-1">
                  {inquiry.attachments.map((file, index) => (
                    <div key={index}>
                      <a href={file} target="_blank" rel="noopener noreferrer" style={{ color: '#00BCD4' }}>
                        {file.split('/').pop()}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <hr />

            <div className="mb-3">
              <h5>문의 내용</h5>
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#2E2E3A', 
                borderRadius: '5px',
                color: '#ccc',
                whiteSpace: 'pre-wrap'
              }}>
                {inquiry.content}
              </div>
            </div>

            {inquiry.admin_response && (
              <div className="mb-3">
                <h5>관리자 답변</h5>
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#3A4B5C', 
                  borderRadius: '5px',
                  color: '#ccc',
                  whiteSpace: 'pre-wrap'
                }}>
                  {inquiry.admin_response}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        {showResponseForm && (
          <Card>
            <Card.Body>
              <h5>답변 작성</h5>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>답변 내용</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="답변 내용을 입력하세요."
                    style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>첨부 파일</Form.Label>
                  <Form.Control
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    style={{ backgroundColor: '#2E2E3A', color: '#fff', border: '1px solid #3A4B5C' }}
                  />
                  {attachments.length > 0 && (
                    <div className="mt-2">
                      <small style={{ color: '#ccc' }}>
                        선택된 파일: {attachments.map(f => f.name).join(', ')}
                      </small>
                    </div>
                  )}
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSubmitResponse}
                    disabled={respondMutation.isPending}
                  >
                    {respondMutation.isPending ? '전송 중...' : '답변 전송'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowResponseForm(false);
                      setResponseText('');
                      setAttachments([]);
                    }}
                  >
                    취소
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default InquiryDetail;

